import React, { useState } from 'react';
import { Tabs as AntdTabs } from 'antd';
import { useTranslation } from '../../../../../core';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../../styles';
import { CHILD_MODALS, TAB_FIELDS, TABS } from '../../utils/constants';
import { AccountSetupTab } from './AccountSetupTab';
import { AdministrationTab } from './AdministrationTab';
import { FeaturesTab } from './FeaturesTab';
import { GeneralTab } from './GeneralTab';
import { SubscriptionTab } from './SubscriptionTab';

/**
 * Organizes company details into its four existing tabs.
 *
 * @param {object} props
 * @param {object} props.company
 * @param {string} props.companyId
 * @param {object} props.formData
 * @param {Function} props.getFieldValue
 * @param {Function} props.hasChanges
 * @param {boolean} props.isDeleting
 * @param {boolean} props.isRemovingSubscription
 * @param {boolean} props.isUpdatingCompany
 * @param {Function} props.onChangeProperty
 * @param {Function} props.onClose
 * @param {Function} props.onOpenChildModal
 * @param {Function} props.onRemoveSubscription
 * @param {Function} props.onSaveFields
 * @param {Array<object>} props.subscriptionPlans
 */
export const CompanyDetailsTabs = ({
    company,
    companyId,
    formData,
    getFieldValue,
    hasChanges,
    isDeleting = false,
    isRemovingSubscription = false,
    isUpdatingCompany = false,
    onChangeProperty,
    onClose,
    onOpenChildModal,
    onRemoveSubscription,
    onSaveFields,
    subscriptionPlans = []
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(TABS.general);

    return (
        <AntdTabs activeKey={activeTab} onChange={setActiveTab} animated={false} className={css(styles.tabs)}>
            <AntdTabs.TabPane tab={t('companies.details.tabs.general')} key={TABS.general}>
                <GeneralTab
                    getFieldValue={getFieldValue}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.general])}
                    isUpdatingCompany={isUpdatingCompany}
                    onChangeProperty={onChangeProperty}
                    onClose={onClose}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.general])}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('companies.accountSetup')} key={TABS.accountSetup}>
                <AccountSetupTab companyId={companyId} onClose={onClose} />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('companies.details.tabs.subscription')} key={TABS.subscription}>
                <SubscriptionTab
                    company={company}
                    formData={formData}
                    getFieldValue={getFieldValue}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.subscription])}
                    isRemovingSubscription={isRemovingSubscription}
                    isUpdatingCompany={isUpdatingCompany}
                    onChangeProperty={onChangeProperty}
                    onClose={onClose}
                    onOpenManualSubscriptionModal={() => onOpenChildModal(CHILD_MODALS.manualSubscription)}
                    onRemoveSubscription={onRemoveSubscription}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.subscription])}
                    subscriptionPlans={subscriptionPlans}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('companies.details.tabs.features')} key={TABS.features}>
                <FeaturesTab
                    companyId={companyId}
                    getFieldValue={getFieldValue}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.features])}
                    isUpdatingCompany={isUpdatingCompany}
                    onChangeProperty={onChangeProperty}
                    onClose={onClose}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.features])}
                />
            </AntdTabs.TabPane>
            <AntdTabs.TabPane tab={t('companies.details.tabs.administration')} key={TABS.administration}>
                <AdministrationTab
                    company={company}
                    companyId={companyId}
                    getFieldValue={getFieldValue}
                    hasChanges={hasChanges(TAB_FIELDS[TABS.administration])}
                    isDeleting={isDeleting}
                    isUpdatingCompany={isUpdatingCompany}
                    onChangeProperty={onChangeProperty}
                    onClose={onClose}
                    onDeleteCompany={() => onOpenChildModal(CHILD_MODALS.delete)}
                    onSave={() => onSaveFields(TAB_FIELDS[TABS.administration])}
                />
            </AntdTabs.TabPane>
        </AntdTabs>
    );
};

const styles = StyleSheet.create({
    tabs: {
        '& .ant-tabs-bar': {
            marginBottom: spacing.s4
        },
        '& .ant-tabs-nav-wrap': {
            overflowX: 'auto',
            overflowY: 'hidden'
        },
        '& .ant-tabs-nav-scroll': {
            overflow: 'visible'
        },
        '& .ant-tabs-nav': {
            display: 'inline-flex',
            whiteSpace: 'nowrap'
        },
        '& .ant-tabs-tab': {
            ...fonts.smNormal,
            marginRight: spacing.s4
        },
        '& .ant-tabs-tab-active': {
            ...fonts.smBold,
            color: colors.primary
        },
        '@media (max-width: 640px)': {
            '& .ant-tabs-tab': {
                marginRight: spacing.s3
            }
        }
    }
});
